<!--
// nuxt-ui/components/pages/players-player/PlayerStats.vue
-->
<script setup lang="ts">
import type { PlayerRanking, PlayerRole } from "~/src/player/domain/Player";
import { $PAGE_GK_PLAYER_STATS, $PAGE_PLAYER_STATS } from "~/src/stats/domain/services/matchStatsService";
import parametersUiService from "~/src/stats/infrastructure/ui-services/parametersUiService";
import type { ParameterCode } from "~/src/stats/domain/models/Parameter";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

type Props = {
  stats: PlayerRanking[];
  playerRole: PlayerRole;
  seasonId: number | undefined;
};
const props = defineProps<Props>();

const { getParameters } = parametersUiService();

const filteredStats = computed((): PlayerRanking[] => {
  if (!props.stats.length) return [];

  if (props.stats && props.stats.length) {
    if (props.playerRole === "goalkeeper") {
      return props.stats.filter(p => $PAGE_GK_PLAYER_STATS.includes(p.parameter.code));
    } else {
      return props.stats.filter(p => $PAGE_PLAYER_STATS.includes(p.parameter.code));
    }
  }
});

const parameters = await getParameters();
const getParameterTranslation = (parameterCode: ParameterCode): string => {
  if (!parameters) return "";
  const parameter = parameters.find(pc => pc.code === parameterCode);
  return parameter?.name || "";
};

const seasonCondition = !$SEASONS_WITHOUT_DATA.includes(Number(props.seasonId));

const allPlayerStats =
  filteredStats.value && filteredStats.value.length
    ? {
        player: {
          doubleGoals: filteredStats.value.find(s => s.parameter.code === "GOL-2X"),
          goalsDice: filteredStats.value.find(s => s.parameter.code === "GOL-DICE"),
          shootoutGoals: filteredStats.value.find(s => s.parameter.code === "GOL-SHO-REG"),
          totalShots: filteredStats.value.find(s => s.parameter.code === "TIR"),
          shotsTarget: filteredStats.value.find(s => s.parameter.code === "TIR-S"),
          shotsWood: filteredStats.value.find(s => s.parameter.code === "TIR-W"),
          assist: filteredStats.value.find(s => s.parameter.code === "ASS-V"),
          passes: filteredStats.value.find(s => s.parameter.code === "PAS"),
          passesSuccess: filteredStats.value.find(s => s.parameter.code === "PAS-R"),
          yellowCards: filteredStats.value.find(s => s.parameter.code === "CRT-G"),
          redCards: filteredStats.value.find(s => s.parameter.code === "CRT-R"),
          dribbling: filteredStats.value.find(s => s.parameter.code === "DRB"),
          dribblingSuccess: filteredStats.value.find(s => s.parameter.code === "DRB-R"),
          fouls: filteredStats.value.find(s => s.parameter.code === "FAL"),
          goalsOwn: filteredStats.value.find(s => s.parameter.code === "GOL-A"),
          penalty: filteredStats.value.find(s => s.parameter.code === "RIG-ALL"),
          penaltyMissed: filteredStats.value.find(s => s.parameter.code === "RIG-F-ALL"),
          shootout: filteredStats.value.find(s => s.parameter.code === "SHO"),
          shootoutMissed: filteredStats.value.find(s => s.parameter.code === "SHO-F"),
        },
        goalkeeper: {
          goalsConceded: filteredStats.value.find(s =>
            seasonCondition ? s.parameter.code === "GOL-S" : s.parameter.code === "GOL-ALLS"
          ),
          shootoutSaved: filteredStats.value.find(s => s.parameter.code === "SHO-P"),
          penaltySaved: filteredStats.value.find(s => s.parameter.code === "RIG-P"),
          stops: filteredStats.value.find(s => s.parameter.code === "PRT"),
        },
      }
    : {
        player: {
          doubleGoals: { parameter: { code: "GOL-2X" }, total: "-" },
          goalsDice: { parameter: { code: "GOL-DICE" }, total: "-" },
          shootoutGoals: { parameter: { code: "GOL-SHO-REG" }, total: "-" },
          totalShots: { parameter: { code: "TIR" }, total: "-" },
          shotsTarget: { parameter: { code: "TIR-S" }, total: "-" },
          shotsWood: { parameter: { code: "TIR-W" }, total: "-" },
          assist: { parameter: { code: "ASS-V" }, total: "-" },
          passes: { parameter: { code: "PAS" }, total: "-" },
          passesSuccess: { parameter: { code: "PAS-R" }, total: "-" },
          yellowCards: { parameter: { code: "CRT-G" }, total: "-" },
          redCards: { parameter: { code: "CRT-R" }, total: "-" },
          dribbling: { parameter: { code: "DRB" }, total: "-" },
          dribblingSuccess: { parameter: { code: "DRB-R" }, total: "-" },
          fouls: { parameter: { code: "FAL" }, total: "-" },
          goalsOwn: { parameter: { code: "GOL-A" }, total: "-" },
          penalty: { parameter: { code: "RIG" }, total: "-" },
          penaltyMissed: { parameter: { code: "RIG-F" }, total: "-" },
          shootout: { parameter: { code: "SHO" }, total: "-" },
          shootoutMissed: { parameter: { code: "SHO-F" }, total: "-" },
        },
        goalkeeper: {
          goalsConceded: seasonCondition
            ? { parameter: { code: "GOL-S" }, total: "-" }
            : { parameter: { code: "GOL-ALLS" }, total: "-" },

          shootoutSaved: {
            parameter: { code: "SHO-P" },
            total: "-",
          },
          penaltySaved: { parameter: { code: "RIG-P" }, total: "-" },
          stops: { parameter: { code: "PRT" }, total: "-" },
        },
      };

const playerStats = ref<PlayerRanking[]>([]);

const gkStats = ref<PlayerRanking[]>([]);

for (const stat in allPlayerStats.player) {
  if (allPlayerStats.player[stat]) {
    playerStats.value.push(allPlayerStats.player[stat]);
    gkStats.value.push(allPlayerStats.player[stat]);
  }
}

for (const stat in allPlayerStats.goalkeeper) {
  if (allPlayerStats.goalkeeper[stat]) gkStats.value.unshift(allPlayerStats.goalkeeper[stat]);
}
</script>

<template>
  <div class="flex gap-1 flex-wrap mt-1" v-if="playerRole === 'goalkeeper' && gkStats.length">
    <div
      v-for="(stat, index) in gkStats"
      :key="index"
      class="rounded-lg lg:h-16 px-5 py-3 lg:py-2 bg-gray-800 lg:bg-gray-900 flex w-full lg:relative items-center lg:justify-between"
    >
      <p class="text-gray-400 text-sm lg:text-center">
        {{ stat ? getParameterTranslation(stat.parameter.code) : stat.parameter.name }}
      </p>
      <p class="lg:text-lg text-xs font-bold ml-auto lg:ml-0">
        {{ stat ? stat.total : 0 }}
      </p>
    </div>
  </div>

  <div class="flex gap-1 flex-wrap mt-1" v-if="playerRole !== 'goalkeeper' && playerStats.length">
    <div
      v-for="(stat, index) in playerStats"
      :key="index"
      class="rounded-lg lg:h-16 px-5 py-3 lg:py-2 bg-gray-800 lg:bg-gray-900 flex w-full lg:relative items-center lg:justify-between"
    >
      <p class="text-gray-400 text-sm lg:text-center">
        {{ stat ? getParameterTranslation(stat.parameter.code) : "-" }}
      </p>
      <p class="lg:text-lg text-xs font-bold ml-auto lg:ml-0">{{ stat ? stat.total : 0 }}</p>
    </div>
  </div>
</template>

<style scoped></style>
